import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import { useAtomValue } from 'jotai';
import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, ButtonGroup } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { fetchWADetail, processJsonData } from './helper';
import WebAppInfoSummary from './WebAppInfoSummary';

type Props = {
  id: number;
};

const WADetailModal = ({ id }: Props) => {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const companyId = useAtomValue(companyIdAtom);

  // assetDetailTabConfig is the configuration for the Asset Detail tab
  const assetDetailTabConfig: ConfigItem[] = [
    { name: 'ID', key: 'webapp_id', type: 'string' },
    { name: 'Name', key: 'name', type: 'string' },
    {
      name: 'Owner',
      key: 'webapp_owner',
      type: 'object',
      config: [
        { name: 'ID', key: 'id', type: 'string' },
        { name: 'Full Name', key: 'fullname', type: 'string' },
        { name: 'Username', key: 'username', type: 'string' },
      ],
    },
    { name: 'Asset URL', key: 'url', type: 'url' },
    { name: 'Operating System', key: 'webapp_os', type: 'string' },
    { name: 'Custom Attributes', key: 'webapp_attributes.list', type: 'list' },
    { name: 'Tags', key: 'detail.tags.list', type: 'list' },
    { name: 'Last Deactivated Date', key: 'last_deactivated_at', type: 'date' },
    { name: 'Last Deactivated By', key: 'deactivated_by', type: 'string' },
    { name: 'Last Reactivated Date', key: 'last_reactivated_at', type: 'date' },
    { name: 'Last Reactivated By', key: 'reactivated_by', type: 'string' },
  ];

  // authTabConfig is the configuration for the Authentication tab
  const authTabConfig: ConfigItem[] = [
    {
      name: 'Authentication Records',
      key: 'webapp_authRecords.list',
      type: 'list',
    },
  ];

  // miscTabConfig is the configuration for the Miscellaneous tab
  const miscTabConfig: ConfigItem[] = [
    { name: 'Active', key: 'is_active', type: 'boolean' },
    {
      name: '',
      key: 'detail',
      type: 'object',
      config: [
        {
          name: 'Comments',
          key: 'comments.list',
          type: 'list',
          listConfig: {
            childNode: 'Comment',
            config: [
              { name: 'Author', key: 'username', type: 'string' },
              { name: 'Comment', key: 'contents', type: 'string' },
              { name: 'Date', key: 'createdDate', type: 'date' },
            ],
          },
        },
        { name: 'Default Scanner', key: 'defaultScanner.type', type: 'string' },
        {
          name: 'DNS Overrides',
          key: 'dnsOverrides',
          type: 'object',
          config: [{ name: 'Count', key: 'count', type: 'number' }],
        },
        { name: 'Screenshot', key: 'screenshot', type: 'image' },
        { name: 'Use Robots', key: 'useRobots', type: 'string' },
        {
          name: 'Logout Regex List',
          key: 'logoutRegexList',
          type: 'object',
          config: [{ name: 'Count', key: 'count', type: 'number' }],
        },
        {
          name: 'Config',
          key: 'config',
          type: 'object',
          config: [
            {
              name: 'Default Auth Record',
              key: 'defaultAuthRecord',
              type: 'object',
            },
          ],
        },
        { name: 'Scheduled', key: 'isScheduled', type: 'boolean' },
        {
          name: 'Crawling Scripts',
          key: 'crawlingScripts',
          type: 'object',
          config: [{ name: 'Count', key: 'count', type: 'number' }],
        },
        {
          name: 'Default Profile',
          key: 'defaultProfile',
          type: 'object',
          config: [
            { name: 'ID', key: 'id', type: 'number' },
            { name: 'Name', key: 'name', type: 'string' },
          ],
        },
        {
          name: 'URL Whitelist',
          key: 'urlWhitelist',
          type: 'object',
          config: [{ name: 'Count', key: 'count', type: 'number' }],
        },
        {
          name: 'URL Blacklist',
          key: 'urlBlacklist',
          type: 'object',
          config: [{ name: 'Count', key: 'count', type: 'number' }],
        },
        {
          name: 'Post Data Blacklist',
          key: 'postDataBlacklist',
          type: 'object',
          config: [{ name: 'Count', key: 'count', type: 'number' }],
        },
        { name: 'Scanner Locked', key: 'scannerLocked', type: 'boolean' },
        { name: 'Scope', key: 'scope', type: 'string' },
        {
          name: 'Malware Scheduling',
          key: 'malwareScheduling',
          type: 'object',
          config: [
            { name: 'Occurence', key: 'occurrence', type: 'object' },
            { name: 'Occurence Type', key: 'occurrenceType', type: 'string' },
            { name: 'Start Date', key: 'startDate', type: 'date' },
            { name: 'Timezone', key: 'timeZone', type: 'object' },
          ],
        },
        {
          name: 'Malware Monitoring',
          key: 'malwareMonitoring',
          type: 'boolean',
        },
        {
          name: 'Malware Notification',
          key: 'malwareMonitoring',
          type: 'boolean',
        },
        { name: 'Use Sitemap', key: 'useSitemap', type: 'boolean' },
        {
          name: 'Created By',
          key: 'createdBy',
          type: 'object',
          config: [
            { name: 'Name', key: 'fullname', type: 'string' },
            { name: 'Username', key: 'username', type: 'number' },
          ],
        },
        {
          name: 'Updated By',
          key: 'updatedBy',
          type: 'object',
          config: [
            { name: 'Name', key: 'fullname', type: 'string' },
            { name: 'Username', key: 'username', type: 'number' },
          ],
        },
      ],
    },
    { name: 'Created at', key: 'webapp_created_date', type: 'date' },
    { name: 'Updated at', key: 'webapp_updated_date', type: 'date' },
  ];

  // miscTabConfig is the configuration for the Miscellaneous tab
  const scanTabConfig: ConfigItem[] = [
    { name: 'Id', key: 'last_scan.id', type: 'string' },
    { name: 'Title', key: 'last_scan.title', type: 'string' },
    { name: 'Ref', key: 'last_scan.ref', type: 'string' },
    {
      name: 'Scanner Appliance',
      key: 'last_scan.scanner_appliance',
      type: 'string',
    },
    { name: 'Start Date', key: 'last_scan.launch_datetime', type: 'date' },
    { name: 'Status', key: 'last_scan.status', type: 'string' },
    { name: 'Duration', key: 'last_scan.duration', type: 'string' },
    // summary: {"resultsStatus": "SUCCESSFUL", "nbRequests": 64080, "crawlDuration": 1902, "testDuration": 10262, "authStatus": "NONE", "linksCrawled": 218}
    {
      name: 'Summary',
      key: 'last_scan.summary',
      type: 'object',
      config: [
        { name: 'Authentication Status', key: 'authStatus', type: 'string' },
        { name: 'Results Status', key: 'resultsStatus', type: 'string' },
        { name: 'Requests Performed', key: 'nbRequests', type: 'string' },
        // TODO: figure out the units , seconds? minutes?
        // { name: 'Crawl Duration', key: 'crawlDuration', type: 'string' },
        { name: 'Links Crawled', key: 'linksCrawled', type: 'string' },
      ],
    },
  ];

  // tabs is the configuration for the tabs which are rendered
  const tabs = [
    {
      title: 'Asset Detail',
      config: assetDetailTabConfig,
      data: data,
    },
    {
      title: 'Authentication',
      config: authTabConfig,
      data: data,
    },
    {
      title: 'Last Scan',
      config: scanTabConfig,
      data: data,
    },
    {
      title: 'Miscellaneous',
      config: miscTabConfig,
      data: data,
    },
  ];

  useEffect(() => {
    fetchWADetail(id, companyId)
      .then((res) => {
        if (res) {
          const processedJson = processJsonData(res);
          setData(processedJson);
        }
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => setIsLoading(false));
  }, [id, companyId]);

  if (isLoading) return <LoadingScreen />;

  return (
    <CustomModalWrapper id="wa-scroll">
      {data && (
        <Fragment>
          <Row>
            <Col sm="6" md="4" lg="3">
              <ButtonGroup
                vertical
                className="btn-group-toggle"
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {tabs.map((tab, index) => (
                  <Button
                    key={`tab-btn-${index}`}
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                    }}
                    color={activeTab === index ? 'primary' : 'secondary'}
                    size="sm"
                    title={tab.title}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col sm="12" md="8" lg="9">
              {tabs.map(
                (tab, index) =>
                  activeTab === index && (
                    <>
                      <Row>
                        <Col sm="12">
                          {tab.data ? (
                            <WebAppInfoSummary
                              data={tab.data}
                              description={undefined}
                              mode={'normal'}
                            />
                          ) : (
                            <></>
                          )}
                        </Col>
                      </Row>
                      <DetailTable
                        key={index}
                        data={tab.data}
                        config={tab.config}
                      />
                    </>
                  ),
              )}
            </Col>
          </Row>
        </Fragment>
      )}
    </CustomModalWrapper>
  );
};

export default WADetailModal;
